/**
 * The conversation, inside the bubble.
 *
 * Sending must not hand the reporter a ticket page. A ticket is the staff
 * artefact — Zendesk puts it plainly: messages become tickets *in the
 * workspace* — while the person who asked only ever sees a conversation, and
 * Intercom's Messages space is permanent in the widget for the same reason.
 * Navigating away turns "I asked a question" into "I filed something", which
 * is the friction being removed.
 */

import CheckIcon from "@mui/icons-material/Check";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useRef } from "react";
import { SupportTicket } from "../../api/support-tickets";
import { formatRelativeTime } from "../../utils/date-formatting";
import SupportComposer from "./support-composer";
import type { ComposerCaret } from "./support-composer-editor";
import type { DraftPicture } from "./support-draft-pictures";
import SupportMessageBody from "./support-message-body";

interface SupportConversationPanelProps {
	ticket: SupportTicket | null;
	reply: string;
	isSending: boolean;
	onReplyChange: (value: string) => void;
	onSendReply: () => void;
	/** The reporter saying they are done with this one. */
	onClose: (ticket: SupportTicket) => void;
	/**
	 * Pictures going out with THIS reply.
	 *
	 * A conversation that could only carry a screenshot in its opening
	 * message made the second one useless: the answer to "which page?" is
	 * usually another picture, and the reporter had to open a whole new
	 * report to send one.
	 */
	pictures: DraftPicture[];
	/** Read when a picture is added, to put its [picture N] line at the cursor. */
	inputRef: React.Ref<ComposerCaret>;
	onAddPicture: (blob: Blob) => void;
	onEditPicture: (key: number) => void;
	onRemovePicture: (key: number) => void;
	/** The composer's own three sources, so a reply offers the same ones. */
	pictureSources: {
		isCapturing: boolean;
		onAddPicture: () => void;
		onAddOtherSurface: (() => void) | null;
	};
}

const SupportConversationPanel: React.FC<SupportConversationPanelProps> = ({
	ticket,
	reply,
	isSending,
	onReplyChange,
	onSendReply,
	onClose,
	pictures,
	inputRef,
	onAddPicture,
	onEditPicture,
	onRemovePicture,
	pictureSources,
}) => {
	// A conversation opens at its newest message, not its oldest.
	//
	// Scrolling once when it opens is not enough, and measuring said so: the
	// pictures have not loaded at that point, so the box is 492px of content
	// in a 492px window and there is nothing to scroll. The image lands, the
	// content grows to 503px, and the reader is left looking at the top.
	//
	// So the bottom is held rather than jumped to: whatever changes the
	// content's height — a picture decoding, an answer arriving — scrolls it
	// down again, until the reader scrolls up themselves. Then it lets go,
	// because someone reading back through a conversation must not be dragged
	// to the end by an image finishing.
	const messagesRef = useRef<HTMLDivElement | null>(null);
	const contentRef = useRef<HTMLDivElement | null>(null);
	const isPinnedToBottom = useRef(true);
	const messageCount = ticket?.messages.length ?? 0;
	const ticketId = ticket?.id ?? null;

	// A conversation the reader has just opened always starts pinned, however
	// they had left the last one.
	useEffect(() => {
		isPinnedToBottom.current = true;
	}, [ticketId]);

	useEffect(() => {
		const box = messagesRef.current;
		const content = contentRef.current;
		if (!box || !content) return;
		const holdBottom = (): void => {
			if (!isPinnedToBottom.current) return;
			box.scrollTop = box.scrollHeight;
			lastScrollTop.current = box.scrollTop;
		};
		holdBottom();
		const observer = new ResizeObserver(holdBottom);
		observer.observe(content);
		return () => observer.disconnect();
	}, [ticketId, messageCount]);

	const lastScrollTop = useRef(0);
	const onMessagesScroll = (): void => {
		const box = messagesRef.current;
		if (!box) return;
		// Only a reader moving UP lets go of the bottom.
		//
		// Measuring the distance to the end instead was wrong, and wrong in a
		// way only a real conversation showed: a picture finishing pushes the
		// content down, the browser fires `scroll` with the old scrollTop
		// against the new scrollHeight, and that reads as "they have scrolled
		// away" — so the very growth the observer exists to follow switched it
		// off. A thread of text reached its height immediately and looked
		// fine; one with screenshots opened 340px short.
		//
		// Growth does not move scrollTop, so this cannot mistake it for
		// intent. A little slack for rounding on the way back.
		const top = box.scrollTop;
		if (top < lastScrollTop.current - 1) isPinnedToBottom.current = false;
		else if (box.scrollHeight - top - box.clientHeight < 24)
			isPinnedToBottom.current = true;
		lastScrollTop.current = top;
	};

	if (!ticket) {
		return (
			<Stack alignItems="center" sx={{ py: 4 }}>
				<CircularProgress size={24} />
			</Stack>
		);
	}

	return (
		<Stack
			sx={{ flex: 1, minHeight: 0 }}
			data-component="support-conversation-panel"
		>
			<Stack
				ref={messagesRef}
				onScroll={onMessagesScroll}
				sx={{
					p: 2,
					flex: 1,
					minHeight: 0,
					overflowY: "auto",
					// A drag past the end of the thread stops at the end of
					// the thread. Without this it chained to the document and
					// scrolled the page the reporter was writing about, which
					// on a phone is the page they can no longer see.
					overscrollBehavior: "contain",
				}}
			>
				{/* The scroller keeps the padding; this inner box carries the
				    spacing and is what the observer measures — a ResizeObserver
				    on the scroller itself would report the window, which never
				    changes, rather than the content, which is the thing that
				    grows when a picture decodes. */}
				<Stack ref={contentRef} spacing={1.5}>
					{ticket.messages.map((message) => (
						<Box
							key={message.id}
							data-component="support-conversation-message"
							sx={{
								alignSelf: message.is_from_reporter
									? "flex-end"
									: "flex-start",
								maxWidth: "90%",
								bgcolor: message.is_from_reporter
									? "primary.main"
									: "action.hover",
								color: message.is_from_reporter
									? "primary.contrastText"
									: "text.primary",
								borderRadius: 2,
								px: 1.5,
								py: 1,
							}}
						>
							<SupportMessageBody
								body={message.body}
								pictures={message.attachments}
							/>
							<Typography variant="caption" sx={{ opacity: 0.7 }}>
								{message.author_name} ·{" "}
								{formatRelativeTime(message.created)}
							</Typography>
						</Box>
					))}

					{/* Where the conversation actually ends, which is where its
				    ending belongs — the same place Intercom and Help Scout put
				    "did that answer your question?". It is not a ticket being
				    resolved and no button should imply it is: the reporter is
				    answering a question about the chat they just read, at the
				    bottom of it, having read it. A header button asked them
				    before they had, and a cross on the tab asked them in the
				    vocabulary of dismissing a window.

				    Always, not only once somebody has answered. Closing is
				    the reporter's alone — independent of the status we set and
				    of whether we have written back. Waiting on our reply made
				    it ours after all: a reporter who realised the problem was
				    their own mistake could not withdraw the report until
				    somebody answered it. */}
					<Stack alignItems="center" spacing={0.5} sx={{ pt: 1 }}>
						<Typography variant="caption" color="text.secondary">
							Is this sorted?
						</Typography>
						<Button
							size="small"
							color="inherit"
							startIcon={<CheckIcon />}
							onClick={() => onClose(ticket)}
							sx={{ textTransform: "none" }}
							data-component="support-conversation-close"
						>
							Yes, close this chat
						</Button>
					</Stack>
				</Stack>
			</Stack>

			<Box sx={{ p: 2, pt: 1, borderTop: 1, borderColor: "divider" }}>
				<SupportComposer
					layout="inline"
					compact
					value={reply}
					onChange={onReplyChange}
					placeholder="Write a reply…"
					onSend={onSendReply}
					isSending={isSending}
					pictures={pictures}
					inputRef={inputRef}
					onAddPicture={onAddPicture}
					onEditPicture={onEditPicture}
					onRemovePicture={onRemovePicture}
					sources={pictureSources}
					dataComponent="support-conversation-reply"
				/>
			</Box>
		</Stack>
	);
};

export default SupportConversationPanel;
